import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { ArrowLeft, Loader2, ClipboardList, Trash2, CheckCircle2, XCircle, RefreshCw, ChevronDown, ChevronRight, Calendar, Image as ImageIcon, Bot, Sparkles, Lock, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BetSlipExportCard from "@/components/BetSlipExportCard";
import { exportNodeToPng } from "@/lib/exportImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = { bg: "var(--bc-bg, #020202)", surface: "var(--bc-panel, #09090B)", highlight: "var(--bc-panel-elev, #141416)", border: "var(--bc-border, rgba(255,255,255,0.07))", text: "var(--bc-ink, #FFFFFF)", muted: "var(--bc-sub, #71717A)", green: "#22C55E", accent: "var(--bc-accent)", cyan: "#06B6D4", blue: "#3B82F6", red: "#EF4444", yellow: "#F59E0B" };
const SELECTIONS_KEY = "bc_my_bets";

const getSessionId = () => {
  let sid = localStorage.getItem("betcracker_session_id");
  if (!sid) { sid = crypto.randomUUID?.() || Math.random().toString(36).slice(2); localStorage.setItem("betcracker_session_id", sid); }
  return sid;
};

const todayStr = () => new Date().toISOString().split("T")[0];

const formatDateLabel = (dateStr) => {
  const today = todayStr();
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];
  if (dateStr === today) return "Oggi";
  if (dateStr === yesterday) return "Ieri";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
};

function BetBadge({ badge }) {
  if (!badge) return <span className="font-data text-[10px] px-2 py-0.5" style={{ background: C.highlight, color: C.muted }}>In attesa</span>;
  const ok = badge === "green";
  return (
    <span className="font-data text-[10px] px-2 py-0.5 flex items-center gap-1"
      style={{ background: ok ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)", color: ok ? C.green : C.red, border: `1px solid ${ok ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}` }}>
      {ok ? <CheckCircle2 size={9} /> : <XCircle size={9} />}
      {ok ? "Corretto" : "Errato"}
    </span>
  );
}

function SelectionCard({ sel, result, onRemove, onOpen }) {
  const r = result || {};
  // Backend returns the per-leg outcome inside ``r.bets[]`` (each item has a
  // ``badge`` field), not as a separate ``bet_badges`` map. Build a lookup
  // by bet label so we can still render the badge next to the saved pick.
  const betBadges = useMemo(() => {
    const m = {};
    for (const b of r.bets || []) {
      if (b?.bet) m[b.bet] = b.badge || null;
    }
    return m;
  }, [r.bets]);
  const comboOk = r.combo_badge;
  // Match is "finished" only when the official status is FT / AET / PEN —
  // not when it's NS / 1H / HT / 2H ecc. Otherwise unstarted matches were
  // being rendered with empty badges / score, hiding real outcomes.
  const isFinished = ["FT", "AET", "PEN"].includes(r.status);
  const scoreHome = r.score_home;
  const scoreAway = r.score_away;
  const hasScore = scoreHome != null && scoreAway != null;

  return (
    <div data-testid={`selection-card-${sel.fixture_id}`}
      style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${isFinished ? (comboOk === "green" ? C.green : comboOk === "red" ? C.red : C.border) : C.border}` }}>
      {/* Match header */}
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={onOpen}
        style={{ borderBottom: `1px solid ${C.highlight}` }}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            {sel.league_flag && <img src={sel.league_flag} alt="" className="w-3 h-2 object-cover" onError={(e) => e.target.style.display = "none"} />}
            <span className="font-data text-[10px] uppercase tracking-wider" style={{ color: C.muted }}>{sel.league}</span>
          </div>
          <div className="flex items-center gap-2">
            {sel.home_logo && <img src={sel.home_logo} alt="" className="w-4 h-4 object-contain" onError={(e) => e.target.style.display = "none"} />}
            <span className="font-heading font-bold text-sm uppercase" style={{ color: C.text }}>{sel.home}</span>
            <span style={{ color: C.muted }} className="font-data text-xs">vs</span>
            {sel.away_logo && <img src={sel.away_logo} alt="" className="w-4 h-4 object-contain" onError={(e) => e.target.style.display = "none"} />}
            <span className="font-heading font-bold text-sm uppercase" style={{ color: C.text }}>{sel.away}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="font-data text-[10px]" style={{ color: C.muted }}>
              {sel.kick_off ? new Date(sel.kick_off).toLocaleString("it-IT", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) : ""}
            </span>
            {isFinished && hasScore && (
              <span className="font-data text-[10px] font-bold tabular-nums" style={{ color: C.cyan }}>
                {scoreHome} – {scoreAway}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isFinished && <BetBadge badge={comboOk} />}
          <button data-testid={`remove-${sel.fixture_id}`} onClick={(e) => { e.stopPropagation(); onRemove(sel.fixture_id); }}
            className="p-1.5 transition-colors" style={{ color: C.muted }}
            onMouseEnter={(e) => e.currentTarget.style.color = C.red}
            onMouseLeave={(e) => e.currentTarget.style.color = C.muted}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {/* Individual bets */}
      <div className="px-4 py-2">
        {(sel.bets || []).map((b, i) => {
          const fairOdds = b.odds && b.odds > 1
            ? Number(b.odds)
            : (b.prob ? 100 / b.prob : null);
          return (
            <div key={b.bet || i} className="flex items-center justify-between py-1.5"
              style={{ borderBottom: i < sel.bets.length - 1 ? `1px solid ${C.highlight}` : "none" }}>
              <div className="flex items-center gap-2">
                <BetBadge badge={betBadges[b.bet] || betBadges[b.type] || null} />
                <span className="font-data text-xs" style={{ color: C.muted }}>{b.bet}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-data text-xs tabular-nums" style={{ color: C.muted }}>{b.prob}%</span>
                {fairOdds && (
                  <span className="font-data text-xs tabular-nums px-1.5 py-0.5"
                    title={b.odds ? "Quota book" : "Quota fair (1/probabilità)"}
                    style={{ color: C.cyan, background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.25)" }}>
                    ×{fairOdds.toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PublicPickRow({ pick, typeLabel, typeColor }) {
  const isCombo = pick.type === "combo";
  const confLevel = (pick.confidence || "MEDIA").toUpperCase();
  const confColor = confLevel === "ALTA" ? C.green : confLevel === "MEDIA" ? C.yellow : C.muted;
  return (
    <div className="py-1.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-data text-[9px] px-1.5 py-0.5 uppercase tracking-wider"
          style={{ background: `${typeColor}1a`, border: `1px solid ${typeColor}66`, color: typeColor }}>
          {typeLabel}
        </span>
        <span className="font-data text-[12px] px-2 py-0.5"
          style={{ background: `${C.cyan}15`, border: `1px solid ${C.cyan}55`, color: C.cyan }}>
          {pick.bet}
        </span>
        <span className="font-data text-[12px] tabular-nums" style={{ color: C.text, fontWeight: 600 }}>
          {isCombo ? "Joint " : ""}{pick.prob}%
        </span>
        {pick.odds && (
          <span className="font-data text-[12px] tabular-nums" style={{ color: C.yellow }}>
            ×{Number(pick.odds).toFixed(2)}
          </span>
        )}
        <span className="font-data text-[9px] px-1.5 py-0.5 uppercase tracking-wider"
          style={{ border: `1px solid ${confColor}66`, color: confColor }}>
          {confLevel}
        </span>
      </div>
      {isCombo && Array.isArray(pick.legs) && pick.legs.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          {pick.legs.map((leg, j) => (
            <span key={j} className="font-data text-[10px] px-1.5 py-0.5"
              style={{ background: C.highlight, border: `1px solid ${C.border}`, color: C.text }}>
              <span style={{ color: C.cyan }}>{leg.bet}</span>
              <span style={{ color: C.muted, margin: "0 0.3rem" }}>·</span>
              <span className="tabular-nums">{leg.prob}%</span>
              {leg.fair_odds && (
                <>
                  <span style={{ color: C.muted, margin: "0 0.3rem" }}>·</span>
                  <span style={{ color: C.yellow }} className="tabular-nums">×{Number(leg.fair_odds).toFixed(2)}</span>
                </>
              )}
            </span>
          ))}
        </div>
      )}
      {pick.reasoning && (
        <div className="mt-1.5 font-data text-[11px] italic" style={{ color: C.muted, lineHeight: 1.45 }}>
          {pick.reasoning}
        </div>
      )}
    </div>
  );
}

function AiDailyPicksPaywall({ data }) {
  const navigate = useNavigate();
  const neon1 = "#06FFE4";
  const neon2 = "#FF00E0";
  const neon3 = "#A78BFA";
  return (
    <div
      data-testid="ai-daily-picks-paywall"
      className="mb-4"
      style={{
        position: "relative",
        background: `linear-gradient(135deg, ${neon3}15 0%, ${neon2}08 50%, ${neon1}10 100%), ${C.surface}`,
        border: `1px solid ${neon3}77`,
        boxShadow: `0 0 22px ${neon3}33, inset 0 0 18px ${neon3}11`,
        padding: "1.25rem",
        overflow: "hidden",
      }}
    >
      {/* Scanline overlay */}
      <div
        aria-hidden
        style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `repeating-linear-gradient(180deg, transparent 0, transparent 3px, rgba(255,255,255,0.018) 3px, rgba(255,255,255,0.018) 4px)`,
        }}
      />
      <div style={{ position: "relative", display: "flex", gap: "0.85rem", alignItems: "flex-start" }}>
        <div
          style={{
            width: 44, height: 44,
            background: `radial-gradient(circle at 50% 40%, ${neon3}33, transparent 70%)`,
            border: `1px solid ${neon3}88`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
            boxShadow: `0 0 12px ${neon3}55`,
          }}
        >
          <Lock size={20} style={{ color: neon3, filter: `drop-shadow(0 0 6px ${neon3})` }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap",
            marginBottom: "0.4rem",
          }}>
            <span
              data-testid="ai-paywall-title"
              style={{
                fontFamily: "'Barlow Condensed',sans-serif",
                fontWeight: 900,
                fontSize: "1.05rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: C.text,
                textShadow: `0 0 8px ${neon3}99`,
              }}
            >
              Schedina AI del Giorno
            </span>
            <span style={{
              padding: "0.12rem 0.5rem",
              background: "rgba(0,0,0,0.5)",
              border: `1px solid ${neon1}88`,
              color: neon1,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.6rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              textShadow: `0 0 6px ${neon1}aa`,
            }}>
              {data.n_picks} picks
            </span>
            <span style={{
              padding: "0.12rem 0.55rem",
              background: `linear-gradient(90deg, ${neon3}33, ${neon2}33)`,
              border: `1px solid ${neon3}aa`,
              color: "#fff",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.6rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              display: "inline-flex", alignItems: "center", gap: "0.3rem",
              textShadow: `0 0 6px ${neon3}`,
            }}>
              <Crown size={9} /> Premium
            </span>
          </div>
          <p style={{
            color: C.text,
            fontFamily: "'DM Sans',sans-serif",
            fontSize: "0.85rem",
            lineHeight: 1.55,
            marginBottom: "0.55rem",
          }}>
            La schedina AI del giorno è una <strong style={{ color: neon3 }}>funzionalità riservata Premium</strong>.
            I {data.n_picks} pronostici di oggi sono già pronti, analizzati da Claude Opus 5 con extended thinking,
            quote ≥ 1.20 e reasoning onnicomprensivo per ogni scelta.
          </p>
          {data.summary && (
            <div style={{
              padding: "0.55rem 0.7rem",
              background: "rgba(0,0,0,0.35)",
              border: `1px dashed ${neon3}66`,
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.7rem",
              color: C.muted,
              fontStyle: "italic",
              marginBottom: "0.85rem",
              lineHeight: 1.45,
            }}>
              &ldquo;{data.summary}&rdquo;
            </div>
          )}
          <button
            data-testid="ai-paywall-cta"
            onClick={() => navigate("/pricing")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.45rem",
              background: `linear-gradient(90deg, ${neon3}, ${neon2})`,
              border: "none",
              color: "#000",
              padding: "0.65rem 1.25rem",
              fontFamily: "'Barlow Condensed',sans-serif",
              fontWeight: 900,
              fontSize: "0.88rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              cursor: "pointer",
              boxShadow: `0 0 18px ${neon3}aa`,
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = `0 0 28px ${neon3}, 0 0 40px ${neon2}66`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "none";
              e.currentTarget.style.boxShadow = `0 0 18px ${neon3}aa`;
            }}
          >
            <Crown size={14} /> Sblocca con Premium
          </button>
          <div style={{
            marginTop: "0.5rem",
            fontFamily: "'JetBrains Mono',monospace",
            fontSize: "0.6rem",
            color: C.muted,
            letterSpacing: "0.08em",
          }}>
            Da €9,99/mese · cancellabile in qualsiasi momento
          </div>
        </div>
      </div>
    </div>
  );
}


function AiDailyPicksCard({ data }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  if (!data || !data.published) return null;
  // Premium paywall: backend stripped the picks and set requires_premium=true
  if (data.requires_premium) return <AiDailyPicksPaywall data={data} />;
  if (!data.picks?.length) return null;
  const violet = "#A78BFA";
  return (
    <div
      data-testid="ai-daily-picks-card"
      className="mb-4"
      style={{
        background: `linear-gradient(135deg, ${violet}0a 0%, transparent 60%), ${C.surface}`,
        border: `1px solid ${violet}55`,
        boxShadow: `0 0 18px ${violet}22 inset`,
      }}
    >
      <div
        onClick={() => setCollapsed((c) => !c)}
        data-testid="ai-daily-picks-toggle"
        className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none"
        style={{ borderBottom: collapsed ? "none" : `1px solid ${violet}33` }}
      >
        <Bot size={14} style={{ color: violet, filter: `drop-shadow(0 0 5px ${violet}aa)` }} />
        <span className="font-heading font-black text-sm uppercase tracking-[0.15em]" style={{ color: violet }}>
          Schedina AI del Giorno
        </span>
        <Sparkles size={12} style={{ color: violet, marginLeft: "auto" }} />
        <span className="font-data text-[10px]" style={{ color: C.muted }}>
          {data.picks.length} picks
        </span>
        {collapsed
          ? <ChevronRight size={14} style={{ color: violet }} />
          : <ChevronDown size={14} style={{ color: violet }} />}
      </div>
      {!collapsed && data.summary && (
        <div className="px-4 py-2.5 font-data text-[12px] italic" style={{ color: C.text, lineHeight: 1.5, borderBottom: `1px solid ${C.highlight}` }}>
          “{data.summary}”
        </div>
      )}
      {!collapsed && (
      <div className="divide-y" style={{ borderColor: C.highlight }}>
        {Object.values((data.picks || []).reduce((acc, p) => {
          const fid = p.fixture_id;
          if (!acc[fid]) acc[fid] = { fixture_id: fid, meta: p, single: null, combo: null };
          if (p.type === "combo") acc[fid].combo = p;
          else acc[fid].single = p;
          return acc;
        }, {})).map((group) => {
          const meta = group.single || group.combo || group.meta;
          return (
            <div
              key={group.fixture_id}
              data-testid={`ai-pick-card-${group.fixture_id}`}
              className="px-4 py-3 cursor-pointer transition-colors"
              onClick={() => navigate(`/match/${group.fixture_id}`)}
              style={{ borderBottom: `1px solid ${C.highlight}`, borderLeft: `3px solid ${violet}` }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${violet}06`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {meta.home_logo && <img src={meta.home_logo} alt="" className="w-4 h-4 object-contain" onError={(e) => e.target.style.display = "none"} />}
                <span className="font-heading font-bold text-[13px] uppercase" style={{ color: C.text }}>{meta.home_team}</span>
                <span style={{ color: C.muted }} className="font-data text-[10px]">vs</span>
                {meta.away_logo && <img src={meta.away_logo} alt="" className="w-4 h-4 object-contain" onError={(e) => e.target.style.display = "none"} />}
                <span className="font-heading font-bold text-[13px] uppercase" style={{ color: C.text }}>{meta.away_team}</span>
                {meta.match_date && (
                  <span
                    className="font-data text-[9px] px-1.5 py-0.5"
                    data-testid={`ai-pick-date-${group.fixture_id}`}
                    style={{
                      color: violet,
                      background: `${violet}12`,
                      border: `1px solid ${violet}55`,
                      letterSpacing: "0.06em",
                    }}
                  >
                    {(() => {
                      try {
                        return new Date(meta.match_date + "T12:00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
                      } catch { return meta.match_date; }
                    })()}
                  </span>
                )}
                <span className="ml-auto font-data text-[9px] uppercase tracking-wider" style={{ color: C.muted }}>{meta.league_name}</span>
              </div>
              {group.single && (
                <PublicPickRow pick={group.single} typeLabel="Singolo" typeColor={violet} />
              )}
              {group.combo && (
                <div className={group.single ? "mt-2" : ""}>
                  <PublicPickRow
                    pick={group.combo}
                    typeLabel={group.combo.combo_family ? `Combo · Fam ${group.combo.combo_family}` : "Combo"}
                    typeColor={C.yellow}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

export default function MieiPronosticiPage() {
  const [selections, setSelections] = useState([]);
  const [backendGroups, setBackendGroups] = useState([]);  // from backend
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedDates, setExpandedDates] = useState(new Set([todayStr()]));
  const [exporting, setExporting] = useState(null); // date being exported
  const [exportPayload, setExportPayload] = useState(null); // {date, bets, results}
  const [aiDaily, setAiDaily] = useState(null); // public AI daily picks card
  const exportRef = useRef(null);
  const navigate = useNavigate();
  const sessionId = getSessionId();

  // Load from localStorage + backend
  const loadAll = useCallback(async () => {
    // 1. localStorage (fast, current session)
    try {
      const stored = JSON.parse(localStorage.getItem(SELECTIONS_KEY) || "[]");
      stored.sort((a, b) => new Date(b.added_at) - new Date(a.added_at));
      setSelections(stored);
    } catch (e) { console.error("[MieiPronostici] Backend offline, using localStorage only:", e); setSelections([]); }

    // 2. Backend (persistent, grouped by date)
    try {
      const res = await axios.get(`${API}/user-bets/${sessionId}`);
      setBackendGroups(res.data.groups || []);
    } catch (e) { console.warn("[MieiPronostici] Backend offline, using localStorage only:", e); /* intentional fallback */ }
  }, [sessionId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load the public AI daily picks card. Sends JWT so backend can decide
  // whether to return real picks (Premium/grandfathered) or paywall meta.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("bc_jwt") || localStorage.getItem("access_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${API}/ai-picks/today`, { headers });
        if (!cancelled) setAiDaily(res.data?.published ? res.data : null);
      } catch {
        if (!cancelled) setAiDaily(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Check results for ALL selections (today + history). When ``fresh`` is
  // true, the backend bypasses the API-Football cache — the "Aggiorna"
  // button uses it so a just-finished match flips to its outcome instantly.
  const checkResults = useCallback(async (fresh = false) => {
    const allBets = backendGroups.flatMap((g) => g.bets);
    // Merge with localStorage (deduplicate by fixture_id)
    const merged = [...allBets];
    selections.forEach((s) => {
      if (!merged.find((m) => m.fixture_id === s.fixture_id)) merged.push(s);
    });
    if (merged.length === 0) return;
    setLoading(true);
    try {
      const url = fresh ? `${API}/check-results?fresh=1` : `${API}/check-results`;
      const res = await axios.post(url, {
        selections: merged.map((s) => ({ fixture_id: s.fixture_id, bets: s.bets, kick_off: s.kick_off }))
      });
      const map = {};
      for (const r of res.data.results) map[r.fixture_id] = r;
      setResults(map);
    } catch (e) { console.error("[MieiPronostici] Failed to save bets:", e); }
    finally { setLoading(false); }
  }, [backendGroups, selections]);

  useEffect(() => {
    if (backendGroups.length > 0 || selections.length > 0) checkResults();
  }, [backendGroups, selections, checkResults]);

  // Auto-refresh ogni 90s se ci sono partite non ancora concluse
  useEffect(() => {
    const hasLive = Object.values(results).some(
      (r) => r.status && !["FT", "AET", "PEN"].includes(r.status)
    );
    if (!hasLive && Object.keys(results).length > 0) return;
    const interval = setInterval(() => checkResults(), 90000);
    return () => clearInterval(interval);
  }, [results, checkResults]);

  // Re-check the moment the tab regains focus — a match can finish while the
  // user is on another tab and we don't want them to wait up to 90s for the
  // outcome to flip green/red.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") checkResults();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [checkResults]);

  const removeSelection = async (fixtureId) => {
    // Remove from localStorage
    const updated = selections.filter((s) => s.fixture_id !== fixtureId);
    localStorage.setItem(SELECTIONS_KEY, JSON.stringify(updated));
    setSelections(updated);
    // Remove from backend
    try { await axios.delete(`${API}/user-bets/${sessionId}/${fixtureId}`); } catch (e) { console.error("[MieiPronostici] Failed to delete bet:", e); }
    // Update backend groups
    setBackendGroups((prev) => prev.map((g) => ({ ...g, bets: g.bets.filter((b) => b.fixture_id !== fixtureId) })).filter((g) => g.bets.length > 0));
  };

  const clearAll = async () => {
    localStorage.setItem(SELECTIONS_KEY, JSON.stringify([]));
    setSelections([]);
    setBackendGroups([]);
    setResults({});
    try { await axios.delete(`${API}/user-bets/${sessionId}`); } catch (e) { console.error("[MieiPronostici] Failed to clear all bets:", e); }
  };

  const toggleDate = (d) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      next.has(d) ? next.delete(d) : next.add(d);
      return next;
    });
  };

  // Export the day's bets as a PNG via the hidden BetSlipExportCard.
  const exportDay = useCallback(async (date, bets) => {
    if (!bets?.length) return;
    setExporting(date);
    setExportPayload({ date, bets, results });
    // Wait two animation frames so the off-screen card has actually painted.
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      if (exportRef.current) {
        await exportNodeToPng(exportRef.current, `bet-cracker-${date}.png`);
      }
    } catch (e) {
      console.error("[MieiPronostici] Export failed:", e);
    } finally {
      setExporting(null);
      // Keep payload around briefly so the user sees the success state.
      setTimeout(() => setExportPayload(null), 200);
    }
  }, [results]);

  // Build grouped structure merging backend + localStorage
  const grouped = useMemo(() => {
    const map = {};
    // Normalize a backend bet into the shape SelectionCard expects.
    const normalize = (b) => ({
      ...b,
      home: b.home || b.home_team || "",
      away: b.away || b.away_team || "",
      league: b.league || b.league_name || "",
    });
    // From backend
    backendGroups.forEach((g) => {
      if (!map[g.date]) map[g.date] = {};
      g.bets.forEach((b) => { map[g.date][b.fixture_id] = normalize(b); });
    });
    // From localStorage (override/add missing)
    selections.forEach((s) => {
      const d = (s.kick_off || s.added_at || todayStr()).split("T")[0];
      if (!map[d]) map[d] = {};
      map[d][s.fixture_id] = { ...s, fixture_id: s.fixture_id };
    });
    // Convert to array sorted by date desc
    return Object.entries(map)
      .map(([date, betsMap]) => ({ date, bets: Object.values(betsMap) }))
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [backendGroups, selections]);

  const totalBets = grouped.reduce((c, g) => c + g.bets.length, 0);
  const todayGroup = grouped.find((g) => g.date === todayStr());
  const todayTotal = todayGroup?.bets.length || 0;
  const todayCorrect = todayGroup?.bets.filter((b) => results[b.fixture_id]?.combo_badge === "green").length || 0;

  return (
    <div style={{ background: C.bg, minHeight: "100vh" }}>
      {/* Header */}
      <div className="sticky top-0 z-10" style={{ background: "var(--bc-panel, rgba(2,2,2,0.95))", borderBottom: `1px solid ${C.border}`, backdropFilter: "blur(16px)" }}>
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-1.5" style={{ color: C.muted }}
            onMouseEnter={(e) => e.currentTarget.style.color = C.text}
            onMouseLeave={(e) => e.currentTarget.style.color = C.muted}>
            <ArrowLeft size={18} />
          </button>
          <ClipboardList size={16} style={{ color: C.cyan }} />
          <span className="font-heading font-black text-sm uppercase tracking-[0.15em]" style={{ color: C.text }}>
            I MIEI PRONOSTICI
          </span>
          {loading && <Loader2 size={14} className="animate-spin ml-auto" style={{ color: C.muted }} />}
          {!loading && totalBets > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <button onClick={() => checkResults(true)} className="flex items-center gap-1.5 px-3 py-1.5 font-data text-xs"
                data-testid="refresh-results-btn"
                style={{ background: C.highlight, border: `1px solid ${C.border}`, color: C.muted }}>
                <RefreshCw size={12} /> Aggiorna
              </button>
              <button onClick={clearAll} className="flex items-center gap-1.5 px-3 py-1.5 font-data text-xs"
                data-testid="clear-all-btn"
                style={{ background: C.highlight, border: `1px solid ${C.border}`, color: C.red }}>
                <Trash2 size={12} /> Cancella tutto
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <AiDailyPicksCard data={aiDaily} />
        {grouped.length === 0 && !loading ? (
          <div className="text-center py-16">
            <ClipboardList size={48} className="mx-auto mb-4 opacity-20" style={{ color: C.muted }} />
            <p className="font-heading font-bold text-lg uppercase tracking-widest mb-2" style={{ color: C.muted }}>
              {aiDaily ? "Nessun pronostico personale" : "Nessun pronostico"}
            </p>
            <p className="font-data text-sm mb-6" style={{ color: C.muted }}>Apri una partita e seleziona i tuoi pronostici</p>
            <button onClick={() => navigate("/")} className="px-6 py-2.5 font-heading font-bold text-xs uppercase tracking-wider"
              style={{ background: C.accent, color: "#020202" }}>
              Vai alle partite
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary strip */}
            {todayTotal > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-2" data-testid="summary-strip">
                <div className="text-center py-3" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                  <div className="font-data text-xl font-bold tabular-nums" style={{ color: C.text }}>{todayTotal}</div>
                  <div className="font-data text-[10px] uppercase tracking-wider mt-0.5" style={{ color: C.muted }}>Oggi</div>
                </div>
                <div className="text-center py-3" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                  <div className="font-data text-xl font-bold tabular-nums" style={{ color: C.green }}>{todayCorrect}</div>
                  <div className="font-data text-[10px] uppercase tracking-wider mt-0.5" style={{ color: C.muted }}>Corretti</div>
                </div>
                <div className="text-center py-3" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
                  <div className="font-data text-xl font-bold tabular-nums" style={{ color: C.muted }}>{totalBets}</div>
                  <div className="font-data text-[10px] uppercase tracking-wider mt-0.5" style={{ color: C.muted }}>Totale</div>
                </div>
              </div>
            )}

            {/* Date groups */}
            {grouped.map((group) => {
              const isToday = group.date === todayStr();
              const isOpen = expandedDates.has(group.date);
              const groupCorrect = group.bets.filter((b) => results[b.fixture_id]?.combo_badge === "green").length;
              const groupFinished = group.bets.filter((b) => ["FT", "AET", "PEN"].includes(results[b.fixture_id]?.status)).length;

              return (
                <div key={group.date} data-testid={`date-group-${group.date}`}>
                  {/* Date header */}
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 transition-colors"
                    style={{ background: isToday ? "var(--bc-accent-soft)" : C.surface, border: `1px solid ${isToday ? "var(--bc-accent-medium)" : C.border}`, borderBottom: isOpen ? "none" : undefined }}
                    onClick={() => toggleDate(group.date)}
                  >
                    <Calendar size={14} style={{ color: isToday ? C.accent : C.muted }} />
                    <span className="font-heading font-bold text-sm uppercase tracking-wider flex-1 text-left"
                      style={{ color: isToday ? C.accent : C.text }}>
                      {formatDateLabel(group.date)}
                    </span>
                    <span className="font-data text-xs px-2 py-0.5" style={{ background: C.highlight, color: C.muted }}>{group.bets.length} scommesse</span>
                    {groupFinished > 0 && (
                      <span className="font-data text-xs px-2 py-0.5" style={{ background: "rgba(34,197,94,0.1)", color: C.green }}>
                        {groupCorrect}/{groupFinished} esatte
                      </span>
                    )}
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`export-day-${group.date}`}
                      onClick={(e) => { e.stopPropagation(); exportDay(group.date, group.bets); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); exportDay(group.date, group.bets); } }}
                      title="Scarica come immagine"
                      className="flex items-center gap-1 px-2 py-1 transition-colors cursor-pointer"
                      style={{
                        color: exporting === group.date ? C.accent : C.muted,
                        border: `1px solid ${exporting === group.date ? "var(--bc-accent-medium)" : C.border}`,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = C.accent; }}
                      onMouseLeave={(e) => { if (exporting !== group.date) e.currentTarget.style.color = C.muted; }}
                    >
                      {exporting === group.date
                        ? <Loader2 size={11} className="animate-spin" />
                        : <ImageIcon size={11} />}
                      <span className="font-data text-[10px] uppercase tracking-wider">Immagine</span>
                    </span>
                    {isOpen ? <ChevronDown size={14} style={{ color: C.muted }} /> : <ChevronRight size={14} style={{ color: C.muted }} />}
                  </button>

                  {/* Cards */}
                  {isOpen && (
                    <div className="space-y-2 pt-2">
                      {group.bets.map((sel) => (
                        <SelectionCard
                          key={sel.fixture_id}
                          sel={sel}
                          result={results[sel.fixture_id]}
                          onRemove={removeSelection}
                          onOpen={() => navigate(`/match/${sel.fixture_id}`)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Hidden export render target — moved off-screen rather than display:none
          so html-to-image actually has a painted node to snapshot. */}
      {exportPayload && (
        <div style={{
          position: "fixed", left: -10000, top: 0,
          pointerEvents: "none", opacity: 0,
        }} aria-hidden>
          <BetSlipExportCard
            ref={exportRef}
            mode="bets"
            date={exportPayload.date}
            bets={exportPayload.bets}
            results={exportPayload.results}
          />
        </div>
      )}
    </div>
  );
}
